import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';

interface AddStudentForm {
  customerName: string;
  customerEmail: string;
  companyName: string;
  tier: 'Starter' | 'Pro' | 'Team';
  paymentMethod: 'credit_card' | 'purchase_order' | 'prepaid';
  paymentReference: string;
  status: 'active' | 'payment_complete_pending_account';
}

const DEFAULT_FORM: AddStudentForm = {
  customerName: '',
  customerEmail: '',
  companyName: '',
  tier: 'Pro',
  paymentMethod: 'purchase_order',
  paymentReference: '',
  status: 'active',
};

const AddStudent: React.FC = () => {
  usePageTitle('Add Student');
  const [form, setForm] = useState<AddStudentForm>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.customerName.trim() || !form.customerEmail.trim()) {
      setError('Name and email are required.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.customerEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch('/api/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: form.customerName.trim(),
          customerEmail: form.customerEmail.trim().toLowerCase(),
          companyName: form.companyName.trim(),
          tier: form.tier,
          paymentMethod: form.paymentMethod,
          paymentReference: form.paymentReference.trim() || `ADMIN-${Date.now()}`,
          status: form.status,
          studentList: [form.customerEmail.trim().toLowerCase()],
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setSuccess(`Subscription created for ${form.customerName} (${data.id}). ${form.status === 'active' ? 'Welcome email sent.' : 'Pending admin approval.'}`);
        setForm(DEFAULT_FORM);
      } else {
        const err = await res.json();
        setError(err.message || 'Failed to create subscription.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Add Student</h1>
          <p className="page-subtitle">Create a subscription for a student without going through checkout</p>
        </div>
      </section>

      <section className="section">
        <div className="section-container" style={{ maxWidth: '600px' }}>
          {success && (
            <div className="alert alert-success" style={{ background: 'var(--green-light, #d4edda)', borderColor: 'var(--green, #28a745)', color: '#155724', marginBottom: '20px' }}>
              <span className="alert-icon">&#10003;</span>
              <div>
                <strong>Student Added!</strong>
                <p>{success}</p>
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => { setSuccess(''); }}>
                    Add Another
                  </button>
                  <Link to="/admin/subscriptions" className="btn btn-outline btn-sm">
                    View Subscriptions
                  </Link>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
              <span className="alert-icon">&#9888;</span>
              <div><strong>{error}</strong></div>
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="filter-label" style={{ display: 'block', marginBottom: '4px' }}>Student Name *</label>
                <input
                  type="text"
                  name="customerName"
                  className="filter-input"
                  style={{ width: '100%' }}
                  placeholder="Jane Doe"
                  value={form.customerName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="filter-label" style={{ display: 'block', marginBottom: '4px' }}>Student Email *</label>
                <input
                  type="email"
                  name="customerEmail"
                  className="filter-input"
                  style={{ width: '100%' }}
                  placeholder="jane@company.com"
                  value={form.customerEmail}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="filter-label" style={{ display: 'block', marginBottom: '4px' }}>Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  className="filter-input"
                  style={{ width: '100%' }}
                  placeholder="Acme Corp"
                  value={form.companyName}
                  onChange={handleChange}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label className="filter-label" style={{ display: 'block', marginBottom: '4px' }}>Tier</label>
                  <select
                    name="tier"
                    className="filter-select"
                    style={{ width: '100%' }}
                    value={form.tier}
                    onChange={handleChange}
                  >
                    <option value="Starter">Starter (1 course)</option>
                    <option value="Pro">Pro (unlimited)</option>
                    <option value="Team">Team (up to 10 students)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="filter-label" style={{ display: 'block', marginBottom: '4px' }}>Payment Method</label>
                  <select
                    name="paymentMethod"
                    className="filter-select"
                    style={{ width: '100%' }}
                    value={form.paymentMethod}
                    onChange={handleChange}
                  >
                    <option value="purchase_order">Purchase Order</option>
                    <option value="prepaid">Prepaid</option>
                    <option value="credit_card">Credit Card</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="filter-label" style={{ display: 'block', marginBottom: '4px' }}>Payment Reference</label>
                <input
                  type="text"
                  name="paymentReference"
                  className="filter-input"
                  style={{ width: '100%' }}
                  placeholder="PO-12345 or invoice number (auto-generated if blank)"
                  value={form.paymentReference}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label className="filter-label" style={{ display: 'block', marginBottom: '4px' }}>Status</label>
                <select
                  name="status"
                  className="filter-select"
                  style={{ width: '100%' }}
                  value={form.status}
                  onChange={handleChange}
                >
                  <option value="active">Active (send welcome email now)</option>
                  <option value="payment_complete_pending_account">Pending Onboarding (requires approval)</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Add Student'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/admin')}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default AddStudent;
